'use client';
import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import { useAuthStore } from '@/store/auth.store';
import { useTheme } from '@/lib/theme';
import { useT } from '@/lib/useT';
import { CopilotPanel } from '@/components/CopilotPanel';
import { GlobalSearch } from '@/components/GlobalSearch';
import {
  LayoutDashboard, Building2, Users, Monitor, FileText, CreditCard, Map, BarChart3,
  MessageCircle, Euro, TrendingUp, ShieldCheck, Shield, ShieldOff, ListTree, Webhook, Warehouse,
  Plug, ScrollText, Bot, Mail, Sun, Moon, LogOut, Cpu, Inbox, MessageSquare, Mailbox, Contact, Search, Newspaper, Truck, Smartphone, Presentation, BookOpen, Database, Siren,
} from 'lucide-react';

const NAV_ITEMS = [
  { href: '/super-admin',                label: 'Overview',      icon: LayoutDashboard },
  { href: '/super-admin/integrations',   label: 'Integrationen', icon: Plug },
  { href: '/super-admin/finance',        label: 'Finance',       icon: Euro },
  { href: '/super-admin/trends',         label: 'Trends',        icon: TrendingUp },
  { href: '/super-admin/compliance',     label: 'Compliance',    icon: ShieldCheck },
  { href: '/super-admin/audit',          label: 'Audit Log',     icon: ScrollText },
  { href: '/super-admin/autopilot',      label: 'Autopilot',     icon: Bot },
  { href: '/super-admin/autopilot-email',label: 'Autopilot Mail',icon: Mailbox },
  { href: '/super-admin/email-agent',    label: 'Email Agent',   icon: Mail },
  { href: '/super-admin/security',       label: 'Security',      icon: Shield },
  { href: '/super-admin/gdpr-requests',  label: 'GDPR Erase',    icon: ShieldOff },
  { href: '/super-admin/queues',         label: 'Queues',        icon: ListTree },
  { href: '/super-admin/webhooks',       label: 'Webhooks',      icon: Webhook },
  { href: '/super-admin/organizations',  label: 'Organizations', icon: Building2 },
  { href: '/super-admin/video-storage',  label: 'Video Storage', icon: Database },
  { href: '/super-admin/incident-archive', label: 'Incident Archive', icon: Siren },
  { href: '/super-admin/chat',           label: 'Support',       icon: MessageCircle },
  { href: '/super-admin/feedback',       label: 'Feedback',      icon: Inbox },
  { href: '/super-admin/contact-messages', label: 'Kontakt',     icon: MessageSquare },
  { href: '/super-admin/contact-info',   label: 'Kontakt-Daten', icon: Contact },
  { href: '/super-admin/mobile-apps',    label: 'Mobile Apps',   icon: Smartphone },
  { href: '/super-admin/ai-local',       label: 'AI Local',      icon: Cpu },
  { href: '/super-admin/map',            label: 'Live Map',      icon: Map },
  { href: '/super-admin/analytics',      label: 'Analytics',     icon: BarChart3 },
  { href: '/super-admin/storage',        label: 'Storage',       icon: Warehouse },
  { href: '/super-admin/plans',          label: 'Plans',         icon: CreditCard },
  { href: '/super-admin/pricing',        label: 'Tarife (€/km)', icon: Truck },
  { href: '/super-admin/users',          label: 'Users',         icon: Users },
  { href: '/super-admin/system',         label: 'System',        icon: Monitor },
  { href: '/super-admin/cms-pages',      label: 'Seiten-Texte',  icon: FileText },
  { href: '/super-admin/demo-slides',    label: 'Demo-Folien',   icon: Presentation },
  { href: '/super-admin/seo',            label: 'SEO',           icon: Search },
  { href: '/super-admin/blog',           label: 'Blog',          icon: Newspaper },
  { href: '/super-admin/docs',           label: 'Docs',          icon: BookOpen },
];

const SIDEBAR_W_EXPANDED = '240px';
const SIDEBAR_W_COLLAPSED = '64px';

function applySidebarWidth(expanded: boolean) {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(
    '--sa-sidebar-w', expanded ? SIDEBAR_W_EXPANDED : SIDEBAR_W_COLLAPSED,
  );
}

export default function SuperAdminLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuthStore();
  const isAuthed = !!user;
  const router = useRouter();
  const pathname = usePathname();
  const { theme, toggle: toggleTheme } = useTheme();
  const t = useT();
  const [mounted, setMounted] = useState(false);

  // Persist expanded state across reloads, mirror to CSS var so main shifts.
  const [expanded, setExpanded] = useState(true);
  useEffect(() => {
    setMounted(true);
    const saved = typeof window !== 'undefined'
      ? localStorage.getItem('logixa_sa_sidebar_expanded')
      : null;
    const e = saved === null ? true : saved === '1';
    setExpanded(e);
    applySidebarWidth(e);
  }, []);
  const toggleSidebar = () => {
    setExpanded((cur) => {
      const next = !cur;
      if (typeof window !== 'undefined') localStorage.setItem('logixa_sa_sidebar_expanded', next ? '1' : '0');
      applySidebarWidth(next);
      return next;
    });
  };

  useEffect(() => {
    if (!mounted) return;
    if (!isAuthed) { router.push('/login'); return; }
    if (user?.role !== 'SUPER_ADMIN') {
      // admin.logixa.one has no /dashboard — bounce non-SUPER_ADMIN
      // users to the app subdomain instead of a same-origin 404.
      window.location.href = 'https://app.logixa.one/dashboard';
    }
  }, [mounted, isAuthed, user, router]);

  if (!mounted) return null;
  if (!isAuthed || user?.role !== 'SUPER_ADMIN') return null;

  const sidebarW = expanded ? SIDEBAR_W_EXPANDED : SIDEBAR_W_COLLAPSED;

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--c-bg)' }}>
      {/* Sidebar */}
      <aside
        className="fixed top-0 left-0 h-screen flex flex-col z-40 transition-[width] duration-200 ease-out"
        style={{
          width: sidebarW,
          background: 'var(--c-panel)',
          borderRight: '1px solid var(--c-border)',
        }}
      >
        {/* Logo header — left-click toggles collapse, right-click goes home */}
        <button
          onClick={toggleSidebar}
          onContextMenu={(e) => { e.preventDefault(); window.location.href = '/super-admin'; }}
          title={expanded ? 'Sidebar einklappen' : 'Sidebar ausklappen (Rechtsklick: Übersicht)'}
          className={`flex items-center gap-3 ${expanded ? 'px-5' : 'justify-center'} py-5 group focus:outline-none`}
          style={{ borderBottom: '1px solid var(--c-border)' }}
        >
          <div className="w-9 h-9 rounded-xl overflow-hidden transition-transform group-hover:scale-110 flex-shrink-0"
            style={{ boxShadow: '0 0 20px rgba(255,214,10,0.3)' }}>
            <img src="/logo.png" alt="LOGIXA" width={36} height={36} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          {expanded && (
            <div className="text-left min-w-0">
              <p className="text-sm font-bold whitespace-nowrap" style={{ color: 'var(--c-heading)' }}>Super Admin</p>
              <p className="text-[10px] tracking-[0.15em] uppercase whitespace-nowrap" style={{ color: 'var(--c-muted)' }}>
                LOGIXA
              </p>
            </div>
          )}
        </button>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto scrollbar-none">
          {NAV_ITEMS.map((item) => {
            const isActive = pathname === item.href;
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                href={item.href}
                title={expanded ? undefined : item.label}
                className={`relative flex items-center gap-3 ${expanded ? 'px-3' : 'px-0 justify-center'} py-2.5 rounded-xl text-sm font-medium transition-all group`}
                style={{
                  background: isActive ? 'var(--c-accent)' : 'transparent',
                  color: isActive ? '#fff' : 'var(--c-text)',
                }}
              >
                <Icon size={16} className="flex-shrink-0" />
                {expanded ? (
                  <span className="whitespace-nowrap overflow-hidden text-ellipsis">{item.label}</span>
                ) : (
                  <span className="absolute left-full ml-3 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap
                    opacity-0 group-hover:opacity-100 transition-all pointer-events-none z-50"
                    style={{ background: 'var(--c-card)', color: 'var(--c-text)', border: '1px solid var(--c-border2)', boxShadow: '0 8px 24px var(--c-shadow)' }}>
                    {item.label}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom: user info + theme toggle + logout */}
        <div className={`flex flex-col gap-2 ${expanded ? 'px-3' : 'items-center'} pb-4 pt-3`}
          style={{ borderTop: '1px solid var(--c-border)' }}>
          {user && (
            <div className={`flex items-center ${expanded ? 'gap-2 px-2 py-1.5 rounded-lg' : 'justify-center'}`}
              style={expanded ? { background: 'var(--c-hover)' } : undefined}
              title={expanded ? undefined : `${user.name || ''} ${user.surname || ''}`.trim()}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }}>
                {user.name?.[0]}{user.surname?.[0]}
              </div>
              {expanded && (
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: 'var(--c-heading)' }}>
                    {user.name} {user.surname}
                  </p>
                  <p className="text-[10px] truncate" style={{ color: 'var(--c-muted)' }}>{user.email}</p>
                </div>
              )}
            </div>
          )}
          <div className={`flex ${expanded ? 'gap-1' : 'flex-col gap-2 items-center'}`}>
            <button onClick={toggleTheme} title={theme === 'dark' ? t('theme_light') : t('theme_dark')}
              className={`flex items-center justify-center rounded-lg transition-colors hover:bg-white/[0.05] ${expanded ? 'flex-1 h-9' : 'w-9 h-9'}`}
              style={{ color: theme === 'light' ? 'var(--c-accent)' : 'var(--c-muted)' }}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button onClick={logout} title={t('logout')}
              className={`flex items-center justify-center rounded-lg transition-colors hover:bg-red-500/[0.1] ${expanded ? 'flex-1 h-9' : 'w-9 h-9'}`}
              style={{ color: 'var(--c-muted)' }}>
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content — left margin tracks the sidebar width via CSS var */}
      <main className="flex-1 min-h-screen transition-[margin] duration-200 ease-out"
        style={{ marginLeft: sidebarW }}>
        <div className="flex justify-center items-center gap-3 px-6 pt-4">
          <GlobalSearch />
        </div>
        <div className="p-6 pt-2">
          {children}
        </div>
      </main>

      {/* AI Co-pilot floating panel */}
      <CopilotPanel />
    </div>
  );
}
